import { useState } from 'react'
import { motion } from 'framer-motion'
import { Send, CheckCircle, AlertCircle, Loader2, Users, Heart, TrendingUp, Sparkles, Upload, FileText, Briefcase, Clock } from 'lucide-react'
import { Link } from 'react-router-dom'
import SEO from '../components/SEO'

const values = [
  {
    icon: Heart,
    title: 'Passione',
    description: 'Crediamo in quello che facciamo e ci impegniamo ogni giorno per aiutare le famiglie italiane a risparmiare.',
  },
  {
    icon: Users,
    title: 'Lavoro di Squadra',
    description: 'Collaborazione e rispetto reciproco sono alla base del nostro ambiente di lavoro.',
  },
  {
    icon: TrendingUp,
    title: 'Crescita',
    description: 'Investiamo nella formazione e nello sviluppo professionale di ogni membro del team.',
  },
  {
    icon: Sparkles,
    title: 'Innovazione',
    description: 'Cerchiamo sempre nuovi modi per migliorare i nostri servizi e l\'esperienza dei nostri utenti.',
  },
]

const openPositions = [
  {
    title: 'Consulente Energia',
    type: 'Full-time',
    description: 'Cerchiamo consulenti motivati per aiutare i nostri clienti a trovare le migliori offerte luce e gas.',
  },
  {
    title: 'Consulente Telecomunicazioni',
    type: 'Full-time',
    description: 'Entra nel team dedicato alle offerte fibra, internet e streaming per guidare i clienti nella scelta migliore.',
  },
  {
    title: 'Candidatura Spontanea',
    type: 'Full-time / Part-time',
    description: 'Non trovi la posizione giusta? Inviaci il tuo CV e raccontaci cosa sai fare. Siamo sempre alla ricerca di talenti!',
  },
]

export default function LavoraConNoi() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    position: openPositions[0].title,
    message: '',
    website: '', // honeypot
  })
  const [cvFile, setCvFile] = useState<File | null>(null)
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle')
  const [errorMessage, setErrorMessage] = useState('')

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    // Honeypot check
    if (formData.website) {
      setStatus('success')
      return
    }

    // Validation
    if (!formData.name || !formData.email || !formData.message) {
      setErrorMessage('Compila tutti i campi obbligatori')
      setStatus('error')
      return
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    if (!emailRegex.test(formData.email)) {
      setErrorMessage('Inserisci un indirizzo email valido')
      setStatus('error')
      return
    }

    if (cvFile && cvFile.size > 5 * 1024 * 1024) {
      setErrorMessage('Il file CV non deve superare i 5MB')
      setStatus('error')
      return
    }

    setStatus('loading')

    try {
      const submitData = new FormData()
      submitData.append('name', formData.name)
      submitData.append('email', formData.email)
      submitData.append('phone', formData.phone)
      submitData.append('position', formData.position)
      submitData.append('message', formData.message)
      submitData.append('_token', Date.now().toString(36))
      if (cvFile) {
        submitData.append('cv', cvFile)
      }

      const response = await fetch('/send-candidatura.php', {
        method: 'POST',
        body: submitData,
      })

      const data = await response.json()

      if (data.success) {
        setStatus('success')
      } else {
        setErrorMessage(data.message || 'Errore durante l\'invio. Riprova più tardi.')
        setStatus('error')
      }
    } catch {
      setErrorMessage('Errore di connessione. Riprova più tardi.')
      setStatus('error')
    }
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value })
    if (status === 'error') {
      setStatus('idle')
      setErrorMessage('')
    }
  }

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      const allowedTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document']
      if (!allowedTypes.includes(file.type)) {
        setErrorMessage('Formato non supportato. Carica un file PDF o DOC/DOCX.')
        setStatus('error')
        return
      }
      setCvFile(file)
      if (status === 'error') {
        setStatus('idle')
        setErrorMessage('')
      }
    }
  }

  return (
    <div className="overflow-hidden">
      <SEO
        title="Lavora con Noi"
        description="Entra nel team di Bolletta Amica! Scopri le posizioni aperte e invia la tua candidatura. Cerchiamo persone motivate e appassionate."
        canonical="/lavora-con-noi"
        keywords="lavora con noi, carriere bolletta amica, offerte lavoro energia, lavoro consulente, posizioni aperte"
        breadcrumbs={[
          { name: 'Home', url: '/' },
          { name: 'Lavora con Noi', url: '/lavora-con-noi' },
        ]}
      />

      {/* Hero Section */}
      <section className="relative min-h-[60vh] flex items-center pt-20 overflow-hidden">
        {/* Animated Background */}
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-br from-white via-primary/5 to-white" />
          <div className="absolute inset-0 pattern-dots opacity-40" />
          <motion.div
            className="absolute top-20 right-0 w-[600px] h-[600px] bg-primary/10 rounded-full blur-3xl"
            animate={{
              scale: [1, 1.2, 1],
              opacity: [0.3, 0.5, 0.3],
            }}
            transition={{ duration: 8, repeat: Infinity, ease: 'easeInOut' }}
          />
          <motion.div
            className="absolute -bottom-40 -left-40 w-[400px] h-[400px] bg-blue-400/10 rounded-full blur-3xl"
            animate={{
              scale: [1.2, 1, 1.2],
              opacity: [0.2, 0.4, 0.2],
            }}
            transition={{ duration: 10, repeat: Infinity, ease: 'easeInOut' }}
          />
        </div>

        <div className="container-custom relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="inline-flex items-center gap-2 px-4 py-2 bg-primary/10 backdrop-blur-sm border border-primary/20 text-primary-700 font-semibold rounded-full text-sm mb-6"
              >
                <Briefcase className="w-4 h-4" />
                Unisciti al team
              </motion.div>

              <h1 className="heading-xl mb-6">
                Lavora con{' '}
                <span className="text-primary relative">
                  noi
                  <svg className="absolute -bottom-2 left-0 w-full" viewBox="0 0 100 12" fill="none">
                    <path d="M2 10C25 4 75 4 98 10" stroke="#F8B120" strokeWidth="4" strokeLinecap="round" />
                  </svg>
                </span>
              </h1>

              <p className="text-xl text-dark-500 mb-8 max-w-lg">
                Entra a far parte del team di <span className="font-semibold text-dark-700">Bolletta Amica</span> e aiutaci
                a rendere le bollette degli italiani piu leggere!
              </p>

              <a
                href="#posizioni"
                className="btn-primary text-lg px-8 py-4"
              >
                Scopri le posizioni aperte
                <Briefcase className="w-5 h-5" />
              </a>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8, delay: 0.3 }}
              className="relative flex justify-center"
            >
              <img
                src="/carletto/carletto-ufficio.png"
                alt="Carletto al lavoro"
                className="w-full max-w-sm"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="section-padding bg-dark-50">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="inline-block px-4 py-2 bg-primary/10 text-primary-700 font-semibold rounded-full text-sm mb-4">
              I nostri valori
            </span>
            <h2 className="heading-lg mb-4">Perche lavorare con noi</h2>
            <p className="text-xl text-dark-500 max-w-2xl mx-auto">
              In Bolletta Amica crediamo che un ambiente di lavoro positivo e stimolante sia la chiave per offrire il miglior servizio possibile.
            </p>
          </motion.div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <motion.div
                key={value.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="card p-8 text-center"
              >
                <div className="w-16 h-16 bg-primary/10 rounded-2xl flex items-center justify-center mx-auto mb-6">
                  <value.icon className="w-8 h-8 text-primary" />
                </div>
                <h3 className="text-xl font-display font-bold mb-3">{value.title}</h3>
                <p className="text-dark-500">{value.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Open Positions Section */}
      <section id="posizioni" className="section-padding">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="inline-block px-4 py-2 bg-primary/10 text-primary-700 font-semibold rounded-full text-sm mb-4">
              Posizioni aperte
            </span>
            <h2 className="heading-lg mb-4">Stiamo cercando te!</h2>
            <p className="text-xl text-dark-500 max-w-2xl mx-auto">
              Scopri le posizioni aperte e trova il ruolo perfetto per le tue competenze.
            </p>
          </motion.div>

          <div className="max-w-3xl mx-auto space-y-6">
            {openPositions.map((position, index) => (
              <motion.div
                key={position.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="card p-8"
              >
                <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                  <div className="flex-1">
                    <h3 className="text-xl font-display font-bold mb-2">{position.title}</h3>
                    <div className="flex flex-wrap items-center gap-3 mb-3">
                      <span className="inline-flex items-center gap-1.5 text-sm text-dark-500">
                        <Clock className="w-4 h-4 text-primary" />
                        {position.type}
                      </span>
                    </div>
                    <p className="text-dark-500">{position.description}</p>
                  </div>
                  <a
                    href="#candidatura"
                    onClick={() => setFormData(prev => ({ ...prev, position: position.title }))}
                    className="btn-primary flex-shrink-0"
                  >
                    Candidati
                  </a>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Application Form Section */}
      <section id="candidatura" className="section-padding bg-gradient-to-br from-dark-800 via-dark-900 to-dark-800 relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-0 left-1/4 w-96 h-96 bg-primary/30 rounded-full blur-3xl" />
          <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-blue-500/20 rounded-full blur-3xl" />
        </div>

        <div className="container-custom relative z-10">
          <div className="grid lg:grid-cols-2 gap-16 items-start">
            {/* Left - Info */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <span className="inline-block px-4 py-2 bg-primary/20 text-primary font-semibold rounded-full text-sm mb-4">
                Candidatura
              </span>
              <h2 className="heading-lg text-white mb-6">
                Invia la tua candidatura
              </h2>
              <p className="text-xl text-dark-300 mb-8">
                Compila il modulo con i tuoi dati e allega il tuo CV. Ti ricontatteremo il prima possibile!
              </p>

              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center flex-shrink-0">
                    <FileText className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <h4 className="text-white font-semibold mb-1">CV e Lettera</h4>
                    <p className="text-dark-300 text-sm">Allega il tuo CV in formato PDF o DOC. Una breve presentazione nel messaggio e il gioco e fatto!</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Clock className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <h4 className="text-white font-semibold mb-1">Tempi di Risposta</h4>
                    <p className="text-dark-300 text-sm">Esaminiamo ogni candidatura con attenzione. Riceverai un riscontro entro 5-7 giorni lavorativi.</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Heart className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <h4 className="text-white font-semibold mb-1">Ambiente di Lavoro</h4>
                    <p className="text-dark-300 text-sm">Team giovane e dinamico, possibilita di lavoro da remoto e percorsi di crescita personalizzati.</p>
                  </div>
                </div>
              </div>

              <div className="mt-10">
                <img
                  src="/carletto/3.png"
                  alt="Carletto ti aspetta"
                  className="w-48 mx-auto lg:mx-0"
                />
              </div>
            </motion.div>

            {/* Right - Form */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <div className="bg-white rounded-2xl shadow-2xl p-8">
                {status === 'success' ? (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="text-center py-12"
                  >
                    <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <CheckCircle className="w-8 h-8 text-green-600" />
                    </div>
                    <h3 className="text-xl font-bold text-dark-800 mb-2">
                      Candidatura inviata!
                    </h3>
                    <p className="text-dark-500 mb-6">
                      Grazie per il tuo interesse. Esamineremo la tua candidatura e ti ricontatteremo al piu presto.
                    </p>
                  </motion.div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-4">
                    {/* Honeypot */}
                    <input
                      type="text"
                      name="website"
                      value={formData.website}
                      onChange={handleChange}
                      className="absolute -left-[9999px]"
                      tabIndex={-1}
                      autoComplete="off"
                    />

                    <div>
                      <label className="block text-sm font-medium text-dark-700 mb-1">
                        Nome e Cognome *
                      </label>
                      <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        className="w-full px-4 py-3 border border-dark-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                        placeholder="Mario Rossi"
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-dark-700 mb-1">
                        Email *
                      </label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        className="w-full px-4 py-3 border border-dark-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                        placeholder="mario@esempio.it"
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-dark-700 mb-1">
                        Telefono
                      </label>
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        className="w-full px-4 py-3 border border-dark-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                        placeholder="+39 333 1234567"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-dark-700 mb-1">
                        Posizione di interesse
                      </label>
                      <select
                        name="position"
                        value={formData.position}
                        onChange={handleChange}
                        className="w-full px-4 py-3 border border-dark-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent bg-white"
                      >
                        {openPositions.map((pos) => (
                          <option key={pos.title} value={pos.title}>
                            {pos.title}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-dark-700 mb-1">
                        Curriculum Vitae
                      </label>
                      <label className="flex items-center gap-3 w-full px-4 py-3 border border-dashed border-dark-300 rounded-xl cursor-pointer hover:border-primary hover:bg-primary/5 transition-colors">
                        <Upload className="w-5 h-5 text-dark-400" />
                        <span className="text-dark-500 text-sm">
                          {cvFile ? cvFile.name : 'Carica il tuo CV (PDF, DOC - max 5MB)'}
                        </span>
                        <input
                          type="file"
                          accept=".pdf,.doc,.docx"
                          onChange={handleFileChange}
                          className="hidden"
                        />
                      </label>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-dark-700 mb-1">
                        Presentati brevemente *
                      </label>
                      <textarea
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        rows={4}
                        className="w-full px-4 py-3 border border-dark-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent resize-none"
                        placeholder="Raccontaci di te, delle tue esperienze e perche vorresti lavorare con noi..."
                        required
                      />
                    </div>

                    {status === 'error' && (
                      <motion.div
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-xl text-red-700"
                      >
                        <AlertCircle className="w-5 h-5 flex-shrink-0" />
                        <span className="text-sm">{errorMessage}</span>
                      </motion.div>
                    )}

                    <button
                      type="submit"
                      disabled={status === 'loading'}
                      className="w-full btn-primary py-4 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {status === 'loading' ? (
                        <>
                          <Loader2 className="w-5 h-5 animate-spin" />
                          Invio in corso...
                        </>
                      ) : (
                        <>
                          <Send className="w-5 h-5" />
                          Invia Candidatura
                        </>
                      )}
                    </button>

                    <p className="text-xs text-dark-400 text-center">
                      Inviando questo modulo accetti la nostra{' '}
                      <Link
                        to="/privacy"
                        className="text-primary hover:underline"
                      >
                        Privacy Policy
                      </Link>
                    </p>
                  </form>
                )}
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  )
}
